import type { ReadingMode } from '@lilith-arcana-os/domain';

import type { CombinationPolarity } from '../types';
import { normalizedPairKey } from '../types';
import type { CuratedCombinationKind } from './curated-taxonomy';

export interface CuratedPair {
  id: string;
  a_slug: string;
  b_slug: string;
  kind: CuratedCombinationKind;
  label: string;
  description: string;
  tags: string[];
  polarity: CombinationPolarity;
  intensity: 1 | 2 | 3 | 4 | 5;
  modes_supported: ReadingMode[];
}

const ALL_MODES: ReadingMode[] = ['standard', 'lilith', 'qliphotic', 'kabbalistic'];
const SHADOW_MODES: ReadingMode[] = ['standard', 'lilith', 'qliphotic'];

export const CURATED_PAIRS: CuratedPair[] = [
  {
    id: 'pair-torre-stella',
    a_slug: 'la-torre',
    b_slug: 'la-stella',
    kind: 'healing-after-collapse',
    label: 'Rovina che apre chiarore',
    description: 'La frattura non resta maceria: spinge verso una ricostruzione più limpida.',
    tags: ['collapse', 'healing', 'hope'],
    polarity: 'transformation',
    intensity: 5,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-diavolo-amanti',
    a_slug: 'il-diavolo',
    b_slug: 'gli-amanti',
    kind: 'desire-vs-choice',
    label: 'Desiderio contro legame scelto',
    description: 'Il magnetismo è forte, ma mette alla prova libertà, consenso e responsabilità.',
    tags: ['desire', 'bond', 'temptation'],
    polarity: 'tension',
    intensity: 5,
    modes_supported: SHADOW_MODES,
  },
  {
    id: 'pair-luna-papessa',
    a_slug: 'la-luna',
    b_slug: 'la-papessa',
    kind: 'mystery-depth',
    label: 'Mistero e profondità intuitiva',
    description: 'L’intuizione si addensa, ma va filtrata con silenzio e disciplina interiore.',
    tags: ['intuition', 'depth', 'mystery'],
    polarity: 'bridge',
    intensity: 4,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-morte-matto',
    a_slug: 'la-morte',
    b_slug: 'il-matto',
    kind: 'ending-new-beginning',
    label: 'Taglio che libera il passo',
    description: 'Una fine netta apre una soglia in cui il nuovo chiede coraggio e leggerezza.',
    tags: ['ending', 'renewal', 'threshold'],
    polarity: 'transformation',
    intensity: 5,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-imperatore-torre',
    a_slug: 'l-imperatore',
    b_slug: 'la-torre',
    kind: 'authority-collapse',
    label: 'Struttura che cede',
    description: 'Il controllo irrigidito viene colpito finché non mostra la sua crepa centrale.',
    tags: ['authority', 'structure', 'collapse'],
    polarity: 'tension',
    intensity: 5,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-diavolo-luna',
    a_slug: 'il-diavolo',
    b_slug: 'la-luna',
    kind: 'illusion-bondage',
    label: 'Fascino oscuro e illusione',
    description: 'L’attrazione si intreccia con paura, proiezione e autoinganno.',
    tags: ['illusion', 'bondage', 'shadow'],
    polarity: 'shadow',
    intensity: 5,
    modes_supported: SHADOW_MODES,
  },
  {
    id: 'pair-sole-mondo',
    a_slug: 'il-sole',
    b_slug: 'il-mondo',
    kind: 'completion-joy',
    label: 'Compimento luminoso',
    description: 'Ciò che era disperso trova forma compiuta e una gioia senza ambiguità centrale.',
    tags: ['completion', 'joy', 'clarity'],
    polarity: 'reinforcement',
    intensity: 5,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-papessa-eremita',
    a_slug: 'la-papessa',
    b_slug: 'l-eremita',
    kind: 'inner-guidance',
    label: 'Sapere interiore custodito',
    description: 'La verità non chiede rumore: chiede tempo, ascolto e sottrazione.',
    tags: ['study', 'solitude', 'guidance'],
    polarity: 'bridge',
    intensity: 4,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-carro-ruota',
    a_slug: 'il-carro',
    b_slug: 'la-ruota-della-fortuna',
    kind: 'accelerated-destiny',
    label: 'Accelerazione del destino',
    description: 'Volontà e svolta si agganciano: il movimento diventa rapido e difficile da arrestare.',
    tags: ['momentum', 'fate', 'movement'],
    polarity: 'transformation',
    intensity: 4,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-forza-diavolo',
    a_slug: 'la-forza',
    b_slug: 'il-diavolo',
    kind: 'shadow-integration',
    label: 'Istinto da governare',
    description: 'Il desiderio non va negato, ma contenuto senza diventare tirannia.',
    tags: ['instinct', 'shadow', 'mastery'],
    polarity: 'shadow',
    intensity: 5,
    modes_supported: SHADOW_MODES,
  },
  {
    id: 'pair-morte-temperanza',
    a_slug: 'la-morte',
    b_slug: 'la-temperanza',
    kind: 'healing-after-collapse',
    label: 'Trasmutazione paziente',
    description: 'Dopo il taglio, la forma nuova richiede miscelazione, respiro e misura.',
    tags: ['alchemy', 'transition', 'healing'],
    polarity: 'transformation',
    intensity: 4,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-giustizia-giudizio',
    a_slug: 'la-giustizia',
    b_slug: 'il-giudizio',
    kind: 'spiritual-opening',
    label: 'Bilancio e richiamo finale',
    description: 'Ciò che è stato pesato torna come chiamata netta a rendere conto.',
    tags: ['reckoning', 'truth', 'awakening'],
    polarity: 'bridge',
    intensity: 4,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-appeso-morte',
    a_slug: 'l-appeso',
    b_slug: 'la-morte',
    kind: 'ending-new-beginning',
    label: 'Sospensione che diventa taglio',
    description: 'Un sacrificio o uno stallo maturano finché non diventa impossibile restare uguali.',
    tags: ['suspension', 'release', 'ending'],
    polarity: 'transformation',
    intensity: 4,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-eremita-stella',
    a_slug: 'l-eremita',
    b_slug: 'la-stella',
    kind: 'inner-guidance',
    label: 'Guida interiore e orientamento',
    description: 'La luce non arriva dall’esterno: si accende quando la solitudine diventa pulita.',
    tags: ['guidance', 'hope', 'inner-light'],
    polarity: 'bridge',
    intensity: 4,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-imperatrice-diavolo',
    a_slug: 'l-imperatrice',
    b_slug: 'il-diavolo',
    kind: 'love-tension',
    label: 'Fertilità e possesso',
    description: 'Il piacere creativo rischia di trasformarsi in attaccamento o eccesso sensoriale.',
    tags: ['pleasure', 'attachment', 'magnetism'],
    polarity: 'tension',
    intensity: 4,
    modes_supported: SHADOW_MODES,
  },
  {
    id: 'pair-torre-giudizio',
    a_slug: 'la-torre',
    b_slug: 'il-giudizio',
    kind: 'healing-after-collapse',
    label: 'Caduta che chiama',
    description: 'La crisi non resta accidente: obbliga a una risposta e a un nuovo assetto.',
    tags: ['collapse', 'calling', 'rebirth'],
    polarity: 'transformation',
    intensity: 5,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-stella-sole',
    a_slug: 'la-stella',
    b_slug: 'il-sole',
    kind: 'completion-joy',
    label: 'Fiducia che diventa chiarezza',
    description: 'Il sollievo iniziale si consolida in una visibilità semplice e vitale.',
    tags: ['hope', 'clarity', 'recovery'],
    polarity: 'reinforcement',
    intensity: 4,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-papa-giustizia',
    a_slug: 'il-papa',
    b_slug: 'la-giustizia',
    kind: 'spiritual-opening',
    label: 'Legge e dottrina',
    description: 'Una visione etica chiede forma, coerenza e responsabilità pratica.',
    tags: ['ethics', 'law', 'teaching'],
    polarity: 'bridge',
    intensity: 3,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-carro-forza',
    a_slug: 'il-carro',
    b_slug: 'la-forza',
    kind: 'accelerated-destiny',
    label: 'Spinta con disciplina',
    description: 'L’avanzata riesce quando la volontà si appoggia a un centro saldo.',
    tags: ['movement', 'discipline', 'drive'],
    polarity: 'reinforcement',
    intensity: 4,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-matto-sole',
    a_slug: 'il-matto',
    b_slug: 'il-sole',
    kind: 'spiritual-opening',
    label: 'Innocenza che si espone',
    description: 'Il salto nel nuovo chiede fiducia, ma anche piena visibilità di ciò che si sceglie.',
    tags: ['beginnings', 'clarity', 'trust'],
    polarity: 'reinforcement',
    intensity: 3,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-papessa-giustizia',
    a_slug: 'la-papessa',
    b_slug: 'la-giustizia',
    kind: 'inner-guidance',
    label: 'Sapere che pesa i fatti',
    description: 'L’intuizione si rafforza solo quando accetta verifica e misura.',
    tags: ['intuition', 'discernment', 'truth'],
    polarity: 'bridge',
    intensity: 3,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-ruota-mondo',
    a_slug: 'la-ruota-della-fortuna',
    b_slug: 'il-mondo',
    kind: 'completion-joy',
    label: 'Ciclo che si chiude bene',
    description: 'Il movimento casuale trova infine una forma compiuta e stabile.',
    tags: ['cycle', 'completion', 'fate'],
    polarity: 'reinforcement',
    intensity: 4,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-imperatore-giustizia',
    a_slug: 'l-imperatore',
    b_slug: 'la-giustizia',
    kind: 'authority-collapse',
    label: 'Ordine da rendere equo',
    description: 'Il potere regge solo se accetta misura, reciprocità e legge.',
    tags: ['authority', 'order', 'balance'],
    polarity: 'bridge',
    intensity: 3,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-imperatrice-stella',
    a_slug: 'l-imperatrice',
    b_slug: 'la-stella',
    kind: 'spiritual-opening',
    label: 'Fecondità che guarisce',
    description: 'La capacità di nutrire torna fertile quando il campo si alleggerisce.',
    tags: ['fertility', 'healing', 'nurture'],
    polarity: 'reinforcement',
    intensity: 4,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-papa-eremita',
    a_slug: 'il-papa',
    b_slug: 'l-eremita',
    kind: 'inner-guidance',
    label: 'Tradizione e solitudine',
    description: 'La guida esterna vale solo se diventa voce interiore verificata.',
    tags: ['tradition', 'solitude', 'study'],
    polarity: 'bridge',
    intensity: 3,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-forza-temperanza',
    a_slug: 'la-forza',
    b_slug: 'la-temperanza',
    kind: 'healing-after-collapse',
    label: 'Contenimento e alchimia',
    description: 'L’energia si stabilizza quando forza e misura lavorano insieme.',
    tags: ['balance', 'healing', 'integration'],
    polarity: 'reinforcement',
    intensity: 3,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-luna-torre',
    a_slug: 'la-luna',
    b_slug: 'la-torre',
    kind: 'illusion-bondage',
    label: 'Illusione che crolla',
    description: 'Il non detto si incrina finché il sistema non può più sostenere il travestimento.',
    tags: ['illusion', 'collapse', 'revelation'],
    polarity: 'shadow',
    intensity: 5,
    modes_supported: SHADOW_MODES,
  },
  {
    id: 'pair-sole-giudizio',
    a_slug: 'il-sole',
    b_slug: 'il-giudizio',
    kind: 'completion-joy',
    label: 'Chiarezza che chiama',
    description: 'La luce non basta a rassicurare: chiede anche una risposta matura.',
    tags: ['clarity', 'calling', 'awakening'],
    polarity: 'bridge',
    intensity: 4,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-mondo-papessa',
    a_slug: 'il-mondo',
    b_slug: 'la-papessa',
    kind: 'mystery-depth',
    label: 'Compimento con centro segreto',
    description: 'Una chiusura piena contiene ancora un nucleo da contemplare, non da consumare.',
    tags: ['completion', 'mystery', 'center'],
    polarity: 'bridge',
    intensity: 3,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-diavolo-torre',
    a_slug: 'il-diavolo',
    b_slug: 'la-torre',
    kind: 'authority-collapse',
    label: 'Legame tossico che si spezza',
    description: 'Ciò che teneva prigionieri viene colpito con violenza liberatoria.',
    tags: ['bondage', 'collapse', 'release'],
    polarity: 'shadow',
    intensity: 5,
    modes_supported: SHADOW_MODES,
  },
  {
    id: 'pair-appeso-stella',
    a_slug: 'l-appeso',
    b_slug: 'la-stella',
    kind: 'spiritual-opening',
    label: 'Resa che apre speranza',
    description: 'Quando il controllo si arrende, il campo mostra una via più sottile e respirabile.',
    tags: ['surrender', 'hope', 'opening'],
    polarity: 'bridge',
    intensity: 3,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-giustizia-diavolo',
    a_slug: 'la-giustizia',
    b_slug: 'il-diavolo',
    kind: 'material-lock',
    label: 'Debito e vincolo',
    description: 'Il desiderio incontra la sua conseguenza: il prezzo non può più essere evitato.',
    tags: ['debt', 'shadow', 'consequence'],
    polarity: 'tension',
    intensity: 4,
    modes_supported: SHADOW_MODES,
  },
  {
    id: 'pair-ruota-torre',
    a_slug: 'la-ruota-della-fortuna',
    b_slug: 'la-torre',
    kind: 'authority-collapse',
    label: 'Svolta brusca',
    description: 'Il ciclo cambia in modo così rapido da rendere inevitabile una frattura.',
    tags: ['fate', 'rupture', 'change'],
    polarity: 'transformation',
    intensity: 4,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-papa-amanti',
    a_slug: 'il-papa',
    b_slug: 'gli-amanti',
    kind: 'love-tension',
    label: 'Unione e patto',
    description: 'Il legame cresce quando trova forma etica, parola e riconoscimento reciproco.',
    tags: ['love', 'agreement', 'ethics'],
    polarity: 'bridge',
    intensity: 3,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-papessa-temperanza',
    a_slug: 'la-papessa',
    b_slug: 'la-temperanza',
    kind: 'spiritual-opening',
    label: 'Silenzio alchemico',
    description: 'La trasformazione resta profonda solo se custodita con misura e pazienza.',
    tags: ['alchemy', 'silence', 'patience'],
    polarity: 'bridge',
    intensity: 3,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-imperatrice-sole',
    a_slug: 'l-imperatrice',
    b_slug: 'il-sole',
    kind: 'completion-joy',
    label: 'Espansione fertile',
    description: 'La vitalità trova terreno, corpo e piacere di manifestarsi apertamente.',
    tags: ['fertility', 'joy', 'embodiment'],
    polarity: 'reinforcement',
    intensity: 4,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-eremita-giudizio',
    a_slug: 'l-eremita',
    b_slug: 'il-giudizio',
    kind: 'inner-guidance',
    label: 'Chiamata del saggio interiore',
    description: 'Il richiamo vero emerge dopo un tempo lungo di ascolto e maturazione.',
    tags: ['calling', 'solitude', 'maturity'],
    polarity: 'bridge',
    intensity: 4,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-matto-torre',
    a_slug: 'il-matto',
    b_slug: 'la-torre',
    kind: 'authority-collapse',
    label: 'Salto e rottura',
    description: 'L’azzardo diventa evento irreversibile: il vecchio assetto non regge l’urto.',
    tags: ['risk', 'rupture', 'threshold'],
    polarity: 'tension',
    intensity: 4,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-forza-mondo',
    a_slug: 'la-forza',
    b_slug: 'il-mondo',
    kind: 'completion-joy',
    label: 'Compimento padroneggiato',
    description: 'La chiusura è solida perché l’energia è stata contenuta e resa forma.',
    tags: ['mastery', 'completion', 'integration'],
    polarity: 'reinforcement',
    intensity: 4,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-carro-giudizio',
    a_slug: 'il-carro',
    b_slug: 'il-giudizio',
    kind: 'accelerated-destiny',
    label: 'Richiamo che accelera',
    description: 'Una chiamata non resta astratta: diventa spostamento concreto e visibile.',
    tags: ['calling', 'movement', 'decision'],
    polarity: 'transformation',
    intensity: 4,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-luna-eremita',
    a_slug: 'la-luna',
    b_slug: 'l-eremita',
    kind: 'mystery-depth',
    label: 'Notte e lanterna',
    description: 'Il buio non si dissolve da solo: chiede un procedere lento e vigile.',
    tags: ['night', 'guidance', 'uncertainty'],
    polarity: 'bridge',
    intensity: 4,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-mondo-giudizio',
    a_slug: 'il-mondo',
    b_slug: 'il-giudizio',
    kind: 'spiritual-opening',
    label: 'Compimento chiamato oltre',
    description: 'Anche ciò che è concluso riceve una nuova eco, come se la chiusura fosse una soglia.',
    tags: ['completion', 'calling', 'renewal'],
    polarity: 'transformation',
    intensity: 4,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-papa-papessa',
    a_slug: 'il-papa',
    b_slug: 'la-papessa',
    kind: 'mystery-depth',
    label: 'Dottrina e segreto',
    description: 'Il sapere ufficiale incontra il sapere custodito: tra i due nasce una tensione feconda.',
    tags: ['knowledge', 'tradition', 'secret'],
    polarity: 'bridge',
    intensity: 3,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-imperatore-imperatrice',
    a_slug: 'l-imperatore',
    b_slug: 'l-imperatrice',
    kind: 'love-tension',
    label: 'Forma e fertilità',
    description: 'Potere strutturante e abbondanza creativa possono sostenersi o irrigidirsi a vicenda.',
    tags: ['polarity', 'order', 'fertility'],
    polarity: 'bridge',
    intensity: 3,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-giustizia-mondo',
    a_slug: 'la-giustizia',
    b_slug: 'il-mondo',
    kind: 'completion-joy',
    label: 'Chiusura meritata',
    description: 'Il compimento tiene perché il bilancio è stato affrontato con rigore.',
    tags: ['completion', 'balance', 'closure'],
    polarity: 'reinforcement',
    intensity: 3,
    modes_supported: ALL_MODES,
  },
  {
    id: 'pair-diavolo-morte',
    a_slug: 'il-diavolo',
    b_slug: 'la-morte',
    kind: 'shadow-integration',
    label: 'Catena e recisione',
    description: 'Il vincolo viene portato a visibilità proprio per poter essere tagliato.',
    tags: ['bondage', 'ending', 'shadow-work'],
    polarity: 'transformation',
    intensity: 5,
    modes_supported: SHADOW_MODES,
  },
];

const PAIR_LOOKUP = new Map<string, CuratedPair>(
  CURATED_PAIRS.map((pair) => [normalizedPairKey(pair.a_slug, pair.b_slug), pair]),
);

export const getCuratedPairKey = (aSlug: string, bSlug: string): string =>
  normalizedPairKey(aSlug, bSlug);

export const findCuratedPair = (aSlug: string, bSlug: string): CuratedPair | undefined =>
  PAIR_LOOKUP.get(getCuratedPairKey(aSlug, bSlug));
